import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { getAuthHeaders } from '../utils/auth';
import SEO from '../components/SEO';
import './PaymentSuccess.css';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get transaction details from URL params or state
  const searchParams = new URLSearchParams(location.search);
  const paymentId = searchParams.get('paymentId') || location.state?.paymentId || '';
  
  const [referralCode, setReferralCode] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  // Clean up URL - remove query parameters for clean tracking URL
  React.useEffect(() => {
    if (window.location.search) {
      window.history.replaceState({}, '', '/payment-success');
    }
    
    // Trigger Google Ads conversion tracking for successful payment
    if (paymentId && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17867073014',
        'event_category': 'payment',
        'event_label': 'payment_success',
        'transaction_id': paymentId
      });
    }
  }, [paymentId]);

  // Fetch referral code after payment
  React.useEffect(() => {
    const fetchReferralCode = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.REFERRALS.MY_CODE, {
          method: 'GET',
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setReferralCode(data.referralCode || '');
        }
      } catch (e) {
        console.error('Failed to fetch referral code:', e);
      }
    };
    if (paymentId) fetchReferralCode();
  }, [paymentId]);

  const referralLink = referralCode
    ? `${window.location.origin}/buy-credits?ref=${referralCode}`
    : '';

  const handleCopyReferral = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = referralLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  // Redirect to home if no payment ID is present
  React.useEffect(() => {
    if (!paymentId) {
      navigate('/home');
    }
  }, [paymentId, navigate]);
  
  // Don't render anything if no payment ID
  if (!paymentId) {
    return null;
  }

  return (
    <div className="payment-success-container">
      <SEO 
        title="Payment Successful - BlackKiteAI"
        description="Your payment was successful. Thank you for your purchase!"
        robots="noindex, nofollow"
      />
      
      <div className="payment-success-content">
        <div className="success-icon">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="40" cy="40" r="38" stroke="#48d02a" strokeWidth="4" />
            <path 
              d="M25 40L35 50L55 30" 
              stroke="#48d02a" 
              strokeWidth="4" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="success-title">Payment Successful</h1>
        
        <p className="success-message">
          Thank you for your purchase! Your credits have been added to your account.
        </p>
        
        <p className="success-message" style={{ color: '#48d02a', fontWeight: 600, marginTop: '8px' }}>
          All the best for your interview! 🎯
        </p>

        <div className="transaction-details">
          {paymentId && (
            <div className="detail-row">
              <span className="detail-label">Payment ID:</span>
              <span className="detail-value payment-id">{paymentId}</span>
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button 
            className="btn-primary"
            onClick={() => navigate('/home')}
          >
            Go to Home
          </button>
          
          <button 
            className="btn-secondary"
            onClick={() => navigate('/chat')}
          >
            Start Interview
          </button>
        </div>

        {referralCode && (
          <div className="referral-section-success">
            <h3 className="referral-heading">Share & Earn!</h3>
            <p className="referral-sub">Share your referral code with friends:</p>
            <div className="referral-code-box">
              <span className="referral-code">{referralCode}</span>
              <button className="btn-copy" onClick={handleCopyReferral}>
                {copied ? '✓ Copied!' : '📋 Copy Link'}
              </button>
            </div>
            <div className="referral-share-row">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Practice mock interviews with AI on BlackKiteAI! Use my referral code: ${referralCode} ${referralLink}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn-sm share-wa"
              >
                WhatsApp
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Practice mock interviews with AI on BlackKiteAI! Use my referral code: ${referralCode}`)}&url=${encodeURIComponent(referralLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn-sm share-tw"
              >
                Twitter
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn-sm share-li"
              >
                LinkedIn
              </a>
            </div>
          </div>
        )}

        <div className="support-link">
          <p>
            Need help?{' '}
            <a href="mailto:support@blackkiteai.com">
              support@blackkiteai.com
            </a>
            {' '}or WhatsApp{' '}
            <a href="https://wa.me/917259730473" target="_blank" rel="noopener noreferrer">
              +91 72597 30473
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
